import { ReactElement, useEffect } from "react";
import {
  Accordion,
  Anchor,
  Button,
  Container,
  Flex,
  Loader,
  Stack,
  Text,
  Title,
  useMantineTheme,
} from "@mantine/core";
import AccordionCustom from "@common/components/thirdParty/mantine/accordion/AccordionCustom";
import AccordionHeader from "@common/components/thirdParty/mantine/accordion/AccordionHeader";
import AccordionSection from "@common/components/thirdParty/mantine/accordion/AccordionSection";
import { paymentMethodsStore } from "@stores/payment-methods";
import { billingStore } from "@stores/billing";
import UpdatePaymentMethodForm from "@components/forms/payment/UpdatePaymentMethodForm";
import StripeProvider from "@components/thirdParty/stripe/StripeProvider";
import CreateTaxIdForm from "@components/forms/billing/taxId/CreateTaxIdForm";
import TaxIdsList from "@components/forms/billing/taxId/TaxIdsList";
import InvoicesList from "@components/forms/billing/invoice/InvoicesList";
import { formatAddress, pluralize } from "@common/utils/string";
import { IInvoice } from "@stores/billing/types";
import TitleWithIcon from "@common/components/thirdParty/mantine/text/TitleWithIcon";
import IconInvoicing from "@common/components/icons/IconInvoicing";
import IconTag from "@common/components/icons/IconTag";
import IconCreditCard from "@common/components/icons/IconCreditCard";
import ConfirmationModal from "@components/account/invoicing/ConfirmationModal";
import { useDisclosure } from "@mantine/hooks";
import AccordionControlLink from "@common/components/thirdParty/mantine/accordion/AccordionControlLink";
import { persistUTMQueryParamsToString } from "@common/utils/router";
import { useRouter } from "next/router";
import { BILLING_METHOD_INVOICE_TYPE } from "@stores/session/constants";
import { purchasesStore } from "@stores/purchases";
import CreatePaymentMethodForm from "@components/forms/payment/CreatePaymentMethodForm";
import BillingAddressForm from "@components/forms/billing/address/BillingAddressForm";
import { customerStore } from "@stores/customer";
import WarningCard from "@common/components/thirdParty/mantine/card/WarningCard";
import Link from "next/link";
import PONumberForm from "@components/forms/billing/poNumber/PONumberForm";
import PONumberDetails from "@components/forms/billing/poNumber/PONumberDetails";
import ShippingAddressForm from "@components/forms/billing/address/ShippingAddressForm";
import IconHome from "@common/components/icons/IconHome";
import IconPin from "@common/components/icons/IconPin";
import IconPage from "@common/components/icons/IconPage";
import InfoCard from "@common/components/thirdParty/mantine/card/InfoCard";
import ErrorMessage from "@common/components/text/ErrorMessage";
import { useConfig } from "@stores/config/hooks";
import { useAccount } from "@stores/session/hooks";
import IconCloud from "@common/components/icons/IconCloud";
import IconServer from "@common/components/icons/IconServer";
import { getInstancePathname } from "@stores/instances/utils";
import {
  SUBSCRIPTION_STATUS_ACTIVE,
  SUBSCRIPTION_STATUS_TRIAL,
} from "@stores/subscriptions/constants";
import { formatPlanName } from "@common/utils/plan";
import PageTitle from "@common/components/thirdParty/mantine/text/PageTitle";
import { WhiteSpaceMixin } from "@common/constants/theme-mantine/utils";

export const SECTION_PAYMENT_METHOD = "payment-method";
export const SECTION_INVOICING = "switch-to-invoicing";
export const SECTION_BILLING_ADDRESS = "billing-address";
export const SECTION_SHIPPING_ADDRESS = "shipping-address";
const SECTION_NEW_TAX_ID = "new-tax-id";
const SECTION_PO_NUMBER = "po-number";
const SECTION_INVOICES_LIST_PREFIX = "invoices-";
const SECTION_INVOICES_LIST = `${SECTION_INVOICES_LIST_PREFIX}*`;
const SECTIONS = [
  SECTION_PAYMENT_METHOD,
  SECTION_INVOICING,
  SECTION_BILLING_ADDRESS,
  SECTION_SHIPPING_ADDRESS,
  SECTION_NEW_TAX_ID,
  SECTION_PO_NUMBER,
  SECTION_INVOICES_LIST,
];

export const SHOW_SWITCH_TO_INVOICING_BUTTON = "sstib";

export default function ManageAccountBillingPage(): ReactElement {
  const theme = useMantineTheme();
  const router = useRouter();

  // show the switch to invoicing button
  // IF on localhost
  // AND query param
  const { query } = router;
  const { [SHOW_SWITCH_TO_INVOICING_BUTTON]: showSwitchToInvoicing } = query;
  const showSwitchToInvoicingButton =
    window.location.host.includes("localhost") && !!showSwitchToInvoicing;

  // session
  const account = useAccount();

  // config
  const config = useConfig();

  // customer
  const { isLoading: customerDetailsLoading, data: customerDetails } =
    customerStore.useGetDetails();

  // payment
  const { isLoading: loadingDefaultPaymentMethod, data: defaultPaymentMethod } =
    paymentMethodsStore.useDefaultPaymentMethod();

  // purchases
  const {
    isLoading: loadingPurchases,
    error: purchasesError,
    refetch: refetchPurchases,
    data: purchases,
  } = purchasesStore.useGetPurchases({ enabled: false });
  const filteredPurchases = purchases?.filter(
    ({ subscription }) =>
      subscription.status === SUBSCRIPTION_STATUS_ACTIVE ||
      subscription.status === SUBSCRIPTION_STATUS_TRIAL,
  );

  useEffect(() => {
    if (account?.is_eligible_for_invoicing) {
      refetchPurchases();
    }
  }, [refetchPurchases, account]);

  // invoicing
  const [
    invoicingModalOpened,
    { open: openInvoicingModal, close: closeInvoicingModal },
  ] = useDisclosure(false);

  // tax ids
  const { isLoading: isTaxIDsLoading, data: taxIds } =
    billingStore.useGetTaxIds();

  // invoices
  const {
    isLoading: isInvoicesLoading,
    error: invoicesLoadingError,
    data: invoices = [],
  } = billingStore.useGetInvoices();
  const invoicesByYear: Record<string, IInvoice[]> = invoices.reduce(
    (group, invoiceObj) => {
      const { created } = invoiceObj;
      const [category] = created.split("-");
      group[category] = group[category] ?? [];
      group[category].push(invoiceObj);

      return group;
    },
    {},
  );

  const isPaymentMethodSectionLoading =
    loadingDefaultPaymentMethod || customerDetailsLoading;

  return (
    <Container>
      <PageTitle>Billing</PageTitle>

      <Stack spacing="lg">
        {/* Subscriptions */}
        {loadingPurchases && <Loader size="sm" />}
        {filteredPurchases && filteredPurchases.length > 0 && (
          <AccordionCustom>
            <AccordionHeader
              title={pluralize("Subscriptions", filteredPurchases.length)}
            >
              <Text fz="sm">
                Cancel and delete your{" "}
                {pluralize("instances", filteredPurchases.length)}.
              </Text>
            </AccordionHeader>

            {filteredPurchases.map(({ id, hosted_instance, product }) => (
              <AccordionSection
                key={id}
                data-testid={`ManageAccountBillingPage-subscription_${id}`}
              >
                <Flex align="center" direction="row" gap="sm" w="100%">
                  <TitleWithIcon
                    icon={
                      hosted_instance ? (
                        <IconCloud color={theme.colors.gray[5]} size={16} />
                      ) : (
                        <IconServer color={theme.colors.gray[5]} size={14} />
                      )
                    }
                    title={formatPlanName(
                      product.plan.short_name ||
                        product.plan.billing_details_name,
                    )}
                    order={2}
                    size="h6"
                    fz="sm"
                  >
                    <Flex
                      align={{ base: "flex-start", sm: "center" }}
                      direction={{ base: "column", sm: "row" }}
                      gap={{ base: 0, sm: "sm" }}
                      justify="flex-end"
                      w="100%"
                    >
                      {hosted_instance && (
                        <Anchor
                          href={getInstancePathname({
                            alias_url: hosted_instance.alias_url,
                            dns_alias: hosted_instance.dns_alias,
                          })}
                          target="_blank"
                          rel="noreferrer"
                          fz="sm"
                          w="100%"
                        >
                          {getInstancePathname({
                            alias_url: hosted_instance.alias_url,
                            dns_alias: hosted_instance.dns_alias,
                          })}
                        </Anchor>
                      )}

                      <Anchor
                        data-testid={`ManageAccountBillingPage-subscription_${id}_link`}
                        component={Link}
                        href={persistUTMQueryParamsToString(
                          router,
                          `/account/${id}/cancel`,
                        )}
                        c="red.2"
                        fw={700}
                        fz="sm"
                        style={WhiteSpaceMixin()}
                      >
                        Cancel subscription
                      </Anchor>
                    </Flex>
                  </TitleWithIcon>
                </Flex>
              </AccordionSection>
            ))}
          </AccordionCustom>
        )}

        {/* Details */}
        <AccordionCustom sections={SECTIONS}>
          <AccordionHeader title="Payment details" />

          {/* payment method */}
          {account?.billing_method === BILLING_METHOD_INVOICE_TYPE ? (
            <AccordionSection>
              <TitleWithIcon
                icon={<IconCreditCard />}
                title="Payment method"
                order={2}
                size="h6"
              >
                <Text fz="sm" m={0}>
                  Invoicing
                </Text>
              </TitleWithIcon>
            </AccordionSection>
          ) : (
            <Accordion.Item value={SECTION_PAYMENT_METHOD}>
              <Accordion.Control>
                <TitleWithIcon
                  icon={<IconCreditCard />}
                  title="Payment method"
                  order={2}
                  size="h6"
                >
                  {isPaymentMethodSectionLoading ? (
                    <Loader size="sm" />
                  ) : (
                    defaultPaymentMethod && (
                      <Text fz="sm" m={0}>
                        {defaultPaymentMethod.type} ending in{" "}
                        {defaultPaymentMethod.last4} (
                        {defaultPaymentMethod.expiration})
                      </Text>
                    )
                  )}
                </TitleWithIcon>
              </Accordion.Control>
              <Accordion.Panel>
                {isPaymentMethodSectionLoading && <Loader mt="xs" />}
                {!isPaymentMethodSectionLoading && account && (
                  <StripeProvider>
                    {defaultPaymentMethod ? (
                      <UpdatePaymentMethodForm account={account} />
                    ) : (
                      <CreatePaymentMethodForm account={account} />
                    )}
                  </StripeProvider>
                )}
              </Accordion.Panel>
            </Accordion.Item>
          )}

          {defaultPaymentMethod && (
            <>
              <Accordion.Item value={SECTION_BILLING_ADDRESS}>
                <Accordion.Control data-testid="billing-address_accordion-section">
                  <TitleWithIcon
                    icon={<IconHome />}
                    title="Billing address"
                    order={2}
                    size="h6"
                  >
                    {customerDetails?.billing_address && config && (
                      <Text fz="sm" m={0}>
                        {formatAddress(
                          customerDetails.billing_address,
                          config.permitted_countries,
                        )}
                      </Text>
                    )}
                  </TitleWithIcon>
                </Accordion.Control>
                <Accordion.Panel>
                  <BillingAddressForm />
                </Accordion.Panel>
              </Accordion.Item>

              <Accordion.Item value={SECTION_SHIPPING_ADDRESS}>
                <Accordion.Control data-testid="shipping-address_accordion-section">
                  <TitleWithIcon
                    icon={<IconPin />}
                    title="Shipping address"
                    order={2}
                    size="h6"
                  >
                    {customerDetails?.shipping_address && config && (
                      <Text fz="sm" m={0}>
                        {formatAddress(
                          customerDetails.shipping_address,
                          config.permitted_countries,
                        )}
                      </Text>
                    )}
                  </TitleWithIcon>
                </Accordion.Control>
                <Accordion.Panel>
                  <ShippingAddressForm />
                </Accordion.Panel>
              </Accordion.Item>
            </>
          )}

          {/* tax ids */}
          <Accordion.Item value={SECTION_NEW_TAX_ID}>
            <Accordion.Control>
              <TitleWithIcon
                icon={<IconPage />}
                title="Invoice Tax IDs"
                order={2}
                size="h6"
              >
                {isTaxIDsLoading ? (
                  <Loader size="sm" />
                ) : (
                  taxIds && taxIds.length > 0 && <TaxIdsList taxIds={taxIds} />
                )}
              </TitleWithIcon>
            </Accordion.Control>
            <Accordion.Panel>
              {isTaxIDsLoading ? <Loader mt="xs" /> : <CreateTaxIdForm />}
            </Accordion.Panel>
          </Accordion.Item>

          {/* PO number */}
          <Accordion.Item value={SECTION_PO_NUMBER}>
            <Accordion.Control>
              <TitleWithIcon
                icon={<IconTag />}
                title="PO number"
                order={2}
                size="h6"
              >
                {customerDetailsLoading ? (
                  <Loader size="sm" />
                ) : (
                  customerDetails?.purchase_order_number && (
                    <PONumberDetails
                      purchaseOrderNumber={
                        customerDetails.purchase_order_number
                      }
                    />
                  )
                )}
              </TitleWithIcon>
            </Accordion.Control>
            <Accordion.Panel>
              <PONumberForm />
            </Accordion.Panel>
          </Accordion.Item>

          {/* Switch to invoicing */}
          {account?.billing_method !== BILLING_METHOD_INVOICE_TYPE &&
            account?.is_eligible_for_invoicing && (
              <>
                <Accordion.Item
                  data-testid="SwitchToInvoicing"
                  value={SECTION_INVOICING}
                >
                  <Accordion.Control>
                    <TitleWithIcon
                      icon={<IconInvoicing />}
                      title="Switch to invoicing"
                      order={2}
                      size="h6"
                    />
                  </Accordion.Control>
                  <Accordion.Panel>
                    {!customerDetails?.billing_address && (
                      <WarningCard withWarningIcon mb="sm">
                        <Text fz="sm">
                          To switch to invoicing, you&apos;ll need to{" "}
                          <Link
                            href={persistUTMQueryParamsToString(
                              router,
                              defaultPaymentMethod
                                ? `/account/manage/billing#section=${SECTION_BILLING_ADDRESS}`
                                : `/account/manage/billing#section=${SECTION_PAYMENT_METHOD}`,
                            )}
                          >
                            add a billing address
                          </Link>
                          .
                        </Text>
                      </WarningCard>
                    )}
                    <InfoCard outline>
                      Receive an invoice and pay with ACH or wire transfer.{" "}
                      <Anchor
                        href={persistUTMQueryParamsToString(
                          router,
                          "https://www.metabase.com/docs/latest/cloud/how-billing-works#invoicing-for-pro-plans",
                        )}
                        target="_blank"
                        rel="noreferrer"
                      >
                        Learn more about invoicing
                      </Anchor>
                      .
                    </InfoCard>
                    <Flex
                      align="center"
                      direction="row"
                      justify="flex-start"
                      gap="md"
                    >
                      {showSwitchToInvoicingButton ? (
                        <>
                          <Button
                            data-testid="SwitchToInvoicing-button"
                            mt="md"
                            onClick={() => openInvoicingModal()}
                            loading={loadingPurchases}
                            loaderPosition="right"
                            disabled={
                              !customerDetails?.billing_address ||
                              loadingPurchases
                            }
                          >
                            Switch to invoicing
                          </Button>
                          {purchasesError && (
                            <ErrorMessage>
                              {purchasesError.message || purchasesError.status}
                            </ErrorMessage>
                          )}
                        </>
                      ) : (
                        <Button
                          component={Link}
                          href="mailto:billing@metabase.com"
                          mt="md"
                        >
                          Contact us
                        </Button>
                      )}
                    </Flex>
                  </Accordion.Panel>
                </Accordion.Item>
                <ConfirmationModal
                  purchases={purchases}
                  opened={invoicingModalOpened}
                  onClose={closeInvoicingModal}
                />
              </>
            )}

          {/* invoicing */}
          {account?.billing_method === BILLING_METHOD_INVOICE_TYPE && (
            <>
              <Accordion.Item value={SECTION_PAYMENT_METHOD}>
                <Accordion.Control>
                  <TitleWithIcon
                    icon={<IconInvoicing />}
                    title="Change payment method to credit card"
                    order={2}
                    size="h6"
                  />
                </Accordion.Control>
                <Accordion.Panel>
                  <Text fz="sm">
                    To switch back to paying with a credit card,{" "}
                    <Anchor href="mailto:billing@metabase.com?subject=Switch from invoicing to credit card">
                      contact us
                    </Anchor>
                    .
                  </Text>
                </Accordion.Panel>
              </Accordion.Item>

              <AccordionSection>
                <AccordionControlLink
                  icon={<IconInvoicing />}
                  title="Add additional billing contacts"
                  href={persistUTMQueryParamsToString(
                    router,
                    `/account/manage/settings#section=contacts`,
                  )}
                />
              </AccordionSection>
            </>
          )}
        </AccordionCustom>

        {/* invoices list */}
        <AccordionCustom sections={SECTIONS}>
          {!isInvoicesLoading &&
          !invoicesLoadingError &&
          invoices.length === 0 ? (
            <AccordionHeader title="You don't have any invoices yet">
              <Text fz="sm" mr="xs">
                When you&apos;re billed, they&apos;ll show up here.
              </Text>
            </AccordionHeader>
          ) : (
            <>
              <AccordionHeader title="Invoices" loading={isInvoicesLoading}>
                <Text fz="sm" mr="xs">
                  {invoices.length ? (
                    <>
                      You have {invoices.length}{" "}
                      {pluralize("invoice", invoices.length)}.
                    </>
                  ) : (
                    "No invoices."
                  )}
                </Text>
              </AccordionHeader>

              {!isInvoicesLoading &&
                Object.keys(invoicesByYear)
                  .sort()
                  .reverse()
                  .map((year, index) => (
                    <Accordion.Item
                      key={index}
                      value={`${SECTION_INVOICES_LIST_PREFIX}${year}`}
                    >
                      <Accordion.Control>
                        <Title order={2} size="h6">
                          {year}
                        </Title>
                      </Accordion.Control>
                      <Accordion.Panel>
                        <InvoicesList invoices={invoicesByYear[year]} />
                      </Accordion.Panel>
                    </Accordion.Item>
                  ))}
            </>
          )}
          {invoicesLoadingError && (
            <AccordionHeader title="Invoices">
              <ErrorMessage>
                {invoicesLoadingError.message || invoicesLoadingError.status}
              </ErrorMessage>
            </AccordionHeader>
          )}
        </AccordionCustom>
      </Stack>
    </Container>
  );
}
